<template>
  <div>
    <h3 style="margin-bottom: 20px; color: #33408a; font-weight: bold;">{{ $t('titleSelectionText') }}</h3>

    <!-- Level 1: Field of Study -->
    <el-form-item :label="$t('fieldOfStudyText')">
      <el-autocomplete
        popper-class="correspondence-autocomplete"
        v-model="fieldOfStudyInput"
        :fetch-suggestions="querySearchLevel1"
        :placeholder="$t('search')"
        @select="handleSelectLevel1"
        @clear="clearLevel1"
        clearable
      >
        <template slot-scope="{ item }">
          <div class="link">{{ isGreek ? item.epistimes : item.epistimes_eng }}</div>
        </template>
      </el-autocomplete>
    </el-form-item>

    <!-- Level 2: Title Programme -->
    <el-form-item
      v-if="level2Options.length > 0"
      :label="$t('titleProgrammeText')"
    >
      <el-autocomplete
        popper-class="correspondence-autocomplete"
        v-model="titleProgrammeInput"
        :fetch-suggestions="querySearchLevel2"
        :placeholder="$t('search')"
        @select="handleSelectLevel2"
        @clear="clearLevel2"
        clearable
      >
        <template slot-scope="{ item }">
          <div class="link">{{ isGreek ? item.ptyxia : item.ptyxia_eng }}</div>
        </template>
      </el-autocomplete>
    </el-form-item>

    <!-- Level 3: Specialization -->
    <el-form-item
      v-if="level3Options.length > 0"
      :label="$t('specializationText')"
    >
      <el-autocomplete
        popper-class="correspondence-autocomplete"
        v-model="specializationInput"
        :fetch-suggestions="querySearchLevel3"
        :placeholder="$t('search')"
        @select="handleSelectLevel3"
        @clear="clearLevel3"
        clearable
      >
        <template slot-scope="{ item }">
          <div class="link">{{ isGreek ? item.eidikeyseis : item.eidikeyseis_eng }}</div>
        </template>
      </el-autocomplete>
    </el-form-item>

    <!-- Level 4: Sub-specialization -->
    <el-form-item
      v-if="level4Options.length > 0"
      :label="$t('subSpecializationText')"
    >
      <el-autocomplete
        popper-class="correspondence-autocomplete"
        v-model="subSpecializationInput"
        :fetch-suggestions="querySearchLevel4"
        :placeholder="$t('search')"
        @select="handleSelectLevel4"
        @clear="clearLevel4"
        clearable
      >
        <template slot-scope="{ item }">
          <div class="link">{{ isGreek ? item.eidikeyseis_fourth_level : item.eidikeyseis_fourth_level_eng }}</div>
        </template>
      </el-autocomplete>
    </el-form-item>

    <!-- Breadcrumb -->
    <div class="selection-path">
      <strong>{{ $t('titleText') }}:</strong>
      <br>
      <span>{{ displayTitle }}</span>
    </div>

    <!-- Validation error -->
    <div v-if="showIncompleteError" class="selection-error">
      <i class="el-icon-warning"></i>
      {{ $t('incompleteSelectionMsg') }}
    </div>

    <hr>
  </div>
</template>

<script>
export default {
  props: ['application', 'allDepartments', 'academicAreasAll', 'checkFormTitles', 'applicationFinalSaved'],

  data() {
    return {
      fieldOfStudyInput: '',
      titleProgrammeInput: '',
      specializationInput: '',
      subSpecializationInput: '',

      selectedLevel1: null,
      selectedLevel2: null,
      selectedLevel3: null,
      selectedLevel4: null,

      level2Options: [],
      level3Options: [],
      level4Options: [],

      showIncompleteError: false,
    }
  },

  computed: {
    isGreek() {
      return this.$i18n.locale === 'el'
    },

    isLegacyApplication() {
      const tc = this.application.titlesCorrespondence
      const hasUniversityDepartment =
        tc.universityDepartment?.id !== null &&
        tc.universityDepartment?.id !== undefined
      const hasNoNewFields =
        (tc.fieldOfStudy?.id === null || tc.fieldOfStudy?.id === undefined) &&
        (tc.titleProgramme?.id === null || tc.titleProgramme?.id === undefined) &&
        (tc.specialization?.id === null || tc.specialization?.id === undefined) &&
        (tc.subSpecialization?.id === null || tc.subSpecialization?.id === undefined)
      return hasUniversityDepartment && hasNoNewFields
    },

    checkSelection() {
      if (this.applicationFinalSaved) return false
      if (this.application.applicationType !== 'recognition') return false
      if (this.application.degreeType !== 'bachelor') return false
      return true
    },

    isSelectionIncomplete() {
      if (!this.checkSelection) return false
      if (!this.selectedLevel1) return true
      if (this.level2Options.length > 0 && !this.selectedLevel2) return true
      if (this.level3Options.length > 0 && !this.selectedLevel3) return true
      if (this.level4Options.length > 0 && !this.selectedLevel4) return true
      return false
    },

    selectionPath() {
      const parts = []
      if (this.selectedLevel1) parts.push(this.selectedLevel1.epistimes)
      if (this.selectedLevel2) parts.push(this.selectedLevel2.ptyxia)
      if (this.selectedLevel3) parts.push(this.selectedLevel3.eidikeyseis)
      if (this.selectedLevel4) parts.push(this.selectedLevel4.eidikeyseis_fourth_level)
      return parts.length > 0 ? parts.join(' > ') : null
    },

    selectionPathEng() {
      const parts = []
      if (this.selectedLevel1) parts.push(this.selectedLevel1.epistimes_eng)
      if (this.selectedLevel2) parts.push(this.selectedLevel2.ptyxia_eng)
      if (this.selectedLevel3) parts.push(this.selectedLevel3.eidikeyseis_eng)
      if (this.selectedLevel4) parts.push(this.selectedLevel4.eidikeyseis_fourth_level_eng)
      return parts.length > 0 ? parts.join(' > ') : null
    },

    selectionPathLocalized() {
      const parts = []
      if (this.selectedLevel1) parts.push(this.isGreek ? this.selectedLevel1.epistimes : this.selectedLevel1.epistimes_eng)
      if (this.selectedLevel2) parts.push(this.isGreek ? this.selectedLevel2.ptyxia : this.selectedLevel2.ptyxia_eng)
      if (this.selectedLevel3) parts.push(this.isGreek ? this.selectedLevel3.eidikeyseis : this.selectedLevel3.eidikeyseis_eng)
      if (this.selectedLevel4) parts.push(this.isGreek ? this.selectedLevel4.eidikeyseis_fourth_level : this.selectedLevel4.eidikeyseis_fourth_level_eng)
      return parts.length > 0 ? parts.join(' > ') : null
    },

    displayTitle() {
      if (this.selectionPathLocalized) {
        return this.selectionPathLocalized
      }
      if (this.isLegacyApplication) {
        return this.application.titlesCorrespondence.universityDepartment.name
      }
      return '-'
    },
  },

  methods: {
    // ==========================================
    // NORMALIZE & SYNC
    // ==========================================
    normalizeTitlesCorrespondence(tc) {
      if (!tc) return
      const fields = ['fieldOfStudy', 'titleProgramme', 'specialization', 'subSpecialization', 'universityDepartment']
      fields.forEach(field => {
        if (!tc[field]) {
          tc[field] = { id: null, name: null, nameEn: null }
        } else if (!('nameEn' in tc[field])) {
          tc[field].nameEn = null
        }
      })
    },

    syncTitlesCorrespondence() {
      if (this.isLegacyApplication) return

      const tc = this.application.titlesCorrespondence

      tc.fieldOfStudy = this.selectedLevel1
        ? { id: this.selectedLevel1.id, name: this.selectedLevel1.epistimes, nameEn: this.selectedLevel1.epistimes_eng }
        : { id: null, name: null, nameEn: null }

      tc.titleProgramme = this.selectedLevel2
        ? { id: this.selectedLevel2.id, name: this.selectedLevel2.ptyxia, nameEn: this.selectedLevel2.ptyxia_eng }
        : { id: null, name: null, nameEn: null }

      tc.specialization = this.selectedLevel3
        ? { id: this.selectedLevel3.id, name: this.selectedLevel3.eidikeyseis, nameEn: this.selectedLevel3.eidikeyseis_eng }
        : { id: null, name: null, nameEn: null }

      tc.subSpecialization = this.selectedLevel4
        ? { id: this.selectedLevel4.id, name: this.selectedLevel4.eidikeyseis_fourth_level, nameEn: this.selectedLevel4.eidikeyseis_fourth_level_eng }
        : { id: null, name: null, nameEn: null }

      let universityDepartmentId = null
      if (this.selectedLevel4) universityDepartmentId = this.selectedLevel4.id
      else if (this.selectedLevel3) universityDepartmentId = this.selectedLevel3.id
      else if (this.selectedLevel2) universityDepartmentId = this.selectedLevel2.id
      else if (this.selectedLevel1) universityDepartmentId = this.selectedLevel1.id

      tc.universityDepartment = {
        id: universityDepartmentId,
        name: this.selectionPath,
        nameEn: this.selectionPathEng,
      }
    },

    // ==========================================
    // LEVEL 1
    // ==========================================
    querySearchLevel1(queryString, cb) {
      if (queryString) {
        const tokens = queryString.toLowerCase().split(/\s+/)
        const results = this.academicAreasAll.filter(item => {
          const searchText = `${item.epistimes} ${item.epistimes_eng} ${item.id}`.toLowerCase()
          return tokens.every(token => searchText.includes(token))
        })
        cb(results)
      } else {
        cb(this.academicAreasAll)
      }
    },

    handleSelectLevel1(item) {
      this.selectedLevel1 = item
      this.fieldOfStudyInput = this.isGreek ? item.epistimes : item.epistimes_eng
      this.level2Options = item.level2 || []
      this.clearLevel2()
      this.showIncompleteError = this.isSelectionIncomplete
      this.emitChanges()
    },

    clearLevel1() {
      this.fieldOfStudyInput = ''
      this.selectedLevel1 = null
      this.level2Options = []
      this.clearLevel2()
      this.showIncompleteError = this.isSelectionIncomplete
      this.emitChanges()
    },

    // ==========================================
    // LEVEL 2
    // ==========================================
    querySearchLevel2(queryString, cb) {
      if (queryString) {
        const tokens = queryString.toLowerCase().split(/\s+/)
        const results = this.level2Options.filter(item => {
          const searchText = `${item.ptyxia} ${item.ptyxia_eng} ${item.id}`.toLowerCase()
          return tokens.every(token => searchText.includes(token))
        })
        cb(results)
      } else {
        cb(this.level2Options)
      }
    },

    handleSelectLevel2(item) {
      this.selectedLevel2 = item
      this.titleProgrammeInput = this.isGreek ? item.ptyxia : item.ptyxia_eng
      this.level3Options = item.level3 || []
      this.clearLevel3()
      this.syncTitlesCorrespondence()
      this.showIncompleteError = this.isSelectionIncomplete
      this.emitChanges()
    },

    clearLevel2() {
      this.titleProgrammeInput = ''
      this.selectedLevel2 = null
      this.level3Options = []
      this.clearLevel3()
      this.syncTitlesCorrespondence()
      this.showIncompleteError = this.isSelectionIncomplete
      this.emitChanges()
    },

    // ==========================================
    // LEVEL 3
    // ==========================================
    querySearchLevel3(queryString, cb) {
      if (queryString) {
        const tokens = queryString.toLowerCase().split(/\s+/)
        const results = this.level3Options.filter(item => {
          const searchText = `${item.eidikeyseis} ${item.eidikeyseis_eng} ${item.id}`.toLowerCase()
          return tokens.every(token => searchText.includes(token))
        })
        cb(results)
      } else {
        cb(this.level3Options)
      }
    },

    handleSelectLevel3(item) {
      this.selectedLevel3 = item
      this.specializationInput = this.isGreek ? item.eidikeyseis : item.eidikeyseis_eng
      this.level4Options = item.level4 || []
      this.clearLevel4()
      this.syncTitlesCorrespondence()
      this.showIncompleteError = this.isSelectionIncomplete
      this.emitChanges()
    },

    clearLevel3() {
      this.specializationInput = ''
      this.selectedLevel3 = null
      this.level4Options = []
      this.clearLevel4()
      this.syncTitlesCorrespondence()
      this.showIncompleteError = this.isSelectionIncomplete
      this.emitChanges()
    },

    // ==========================================
    // LEVEL 4
    // ==========================================
    querySearchLevel4(queryString, cb) {
      if (queryString) {
        const tokens = queryString.toLowerCase().split(/\s+/)
        const results = this.level4Options.filter(item => {
          const searchText = `${item.eidikeyseis_fourth_level} ${item.eidikeyseis_fourth_level_eng} ${item.id}`.toLowerCase()
          return tokens.every(token => searchText.includes(token))
        })
        cb(results)
      } else {
        cb(this.level4Options)
      }
    },

    handleSelectLevel4(item) {
      this.selectedLevel4 = item
      this.subSpecializationInput = this.isGreek ? item.eidikeyseis_fourth_level : item.eidikeyseis_fourth_level_eng
      this.syncTitlesCorrespondence()
      this.showIncompleteError = this.isSelectionIncomplete
      this.emitChanges()
    },

    clearLevel4() {
      this.subSpecializationInput = ''
      this.selectedLevel4 = null
      this.syncTitlesCorrespondence()
      this.showIncompleteError = this.isSelectionIncomplete
      this.emitChanges()
    },

    // ==========================================
    // HELPERS
    // ==========================================
    resetSelections() {
      this.fieldOfStudyInput = ''
      this.titleProgrammeInput = ''
      this.specializationInput = ''
      this.subSpecializationInput = ''
      this.selectedLevel1 = null
      this.selectedLevel2 = null
      this.selectedLevel3 = null
      this.selectedLevel4 = null
      this.level2Options = []
      this.level3Options = []
      this.level4Options = []
      this.showIncompleteError = false
    },

    restoreSelection() {
      const tc = this.application.titlesCorrespondence

      if (this.isLegacyApplication) return

      if (!tc.fieldOfStudy?.id) {
        this.syncTitlesCorrespondence()
        return
      }

      const level1Item = this.academicAreasAll.find(item => item.id === tc.fieldOfStudy.id)
      if (level1Item) {
        this.selectedLevel1 = level1Item
        this.fieldOfStudyInput = this.isGreek ? level1Item.epistimes : level1Item.epistimes_eng
        this.level2Options = level1Item.level2 || []

        if (tc.titleProgramme?.id) {
          const level2Item = this.level2Options.find(item => item.id === tc.titleProgramme.id)
          if (level2Item) {
            this.selectedLevel2 = level2Item
            this.titleProgrammeInput = this.isGreek ? level2Item.ptyxia : level2Item.ptyxia_eng
            this.level3Options = level2Item.level3 || []

            if (tc.specialization?.id) {
              const level3Item = this.level3Options.find(item => item.id === tc.specialization.id)
              if (level3Item) {
                this.selectedLevel3 = level3Item
                this.specializationInput = this.isGreek ? level3Item.eidikeyseis : level3Item.eidikeyseis_eng
                this.level4Options = level3Item.level4 || []

                if (tc.subSpecialization?.id) {
                  const level4Item = this.level4Options.find(item => item.id === tc.subSpecialization.id)
                  if (level4Item) {
                    this.selectedLevel4 = level4Item
                    this.subSpecializationInput = this.isGreek ? level4Item.eidikeyseis_fourth_level : level4Item.eidikeyseis_fourth_level_eng
                  }
                }
              }
            }
          }
        }
      }

      this.syncTitlesCorrespondence()
    },

    emitChanges() {
      this.$emit('correspondenceValidEvent', !this.isSelectionIncomplete)
      this.$emit('titlesCorrespondenceChanged', this.application.titlesCorrespondence)
    },
  },

  watch: {
    checkFormTitles: function(newVal) {
      this.showIncompleteError = this.isSelectionIncomplete
      this.$emit('correspondenceValidEvent', !this.isSelectionIncomplete)
    },

    application: {
      handler: function(newVal, oldVal) {
        this.normalizeTitlesCorrespondence(newVal.titlesCorrespondence)
        if (newVal.id !== oldVal.id) {
          this.resetSelections()
          if (this.academicAreasAll && this.academicAreasAll.length > 0) {
            this.restoreSelection()
          }
        }
      },
      deep: true,
    },

    academicAreasAll: {
      handler: function(newVal) {
        if (newVal && newVal.length > 0) {
          this.restoreSelection()
        }
      },
      immediate: true,
    },

    '$i18n.locale': function() {
      if (this.selectedLevel1) this.fieldOfStudyInput = this.isGreek ? this.selectedLevel1.epistimes : this.selectedLevel1.epistimes_eng
      if (this.selectedLevel2) this.titleProgrammeInput = this.isGreek ? this.selectedLevel2.ptyxia : this.selectedLevel2.ptyxia_eng
      if (this.selectedLevel3) this.specializationInput = this.isGreek ? this.selectedLevel3.eidikeyseis : this.selectedLevel3.eidikeyseis_eng
      if (this.selectedLevel4) this.subSpecializationInput = this.isGreek ? this.selectedLevel4.eidikeyseis_fourth_level : this.selectedLevel4.eidikeyseis_fourth_level_eng
    },
  },
}
</script>

<style lang="scss">
.correspondence-autocomplete {
  li {
    line-height: 1.5 !important;
    padding: 12px 15px !important;
    border-bottom: 1px solid #e8e8e8 !important;
    min-height: 45px !important;
    display: flex !important;
    align-items: center !important;
    transition: background-color 0.2s !important;

    &:last-child {
      border-bottom: none !important;
    }

    &:hover {
      background-color: #f5f7fa !important;
    }

    .value {
      text-overflow: clip !important;
      font-size: 14px !important;
      overflow: visible !important;
      color: #6d728b !important;
      white-space: normal !important;
      word-wrap: break-word !important;
      line-height: 1.5 !important;
    }

    .link {
      text-overflow: clip !important;
      font-size: 14px !important;
      color: #33408a !important;
      white-space: normal !important;
      word-wrap: break-word !important;
      flex: 1 !important;
      line-height: 1.5 !important;
    }
  }
}
</style>

<style lang="scss" scoped>
.selection-path {
  margin-top: 15px;
  padding: 10px;
  background-color: #f0f9ff;
  border-left: 4px solid #409eff;
  font-size: 14px;
  line-height: 1.6;
}

.selection-error {
  margin-top: 8px;
  color: #f56c6c;
  font-size: 13px;
  line-height: 1.5;

  i {
    margin-right: 4px;
  }
}

.el-autocomplete {
  width: 440px;
}
</style>
